import myTraderaItemActions from '../state/actions/my-tradera-items';
import featureToggles from '../utils/feature-toggles';
import {
    openNewMessageModal,
    openModal,
    openShareModal
} from 'tradera/state/ui/reducer';
import { types } from './item-actions-helper';
import { toLocalizedUrl } from 'tradera/utils/url';
import { getSrc } from 'tradera/components/item-card/item-card-parts/item-card-image-size';
import { getMessagingPageLink } from 'tradera/apps/my-tradera/app/helpers/messaging-helpers';
import { CANCEL_ITEM_MODAL_ID } from '../components/cancel-item-modal/cancel-item-modal';

const getId = (item) => item.key;
const getIds = (item) => [item.itemId];

// isPrimaryAction = will be primary action unless one before has not already been set as primary action
// Hence, the order of the actions matter.
const commonActions = (item) => ({
    sellSimilar: () => ({
        id: getId(item),
        type: types.LINK,
        label: 'Sälj liknande',
        getTranslatedLabel: (t) => t('myt_itemCard_actions_sellSimilar'),
        url: `/selling/sell-similar/${item.itemId}`,
        gaLabel: 'SellSimilar',
        isVisible: () => true,
        isPrimaryAction: () => true
    })
});

export const activeSellerActions = (item) => {
    const isShopItem = item.listing.itemType === 'Shopitem';
    const common = commonActions(item);

    const translationKeys = {
        shareHeader: 'myt_shareHeader',
        shareMailSubject: 'myt_shareViaMailSubject',
        shareMailBody: 'myt_shareViaMailBody',
        sharePinterestDescription: 'myt_shareViaPinterestDescription',
        shareLinkText: 'myt_shareLink'
    };

    const actions = [
        () => ({
            id: getId(item),
            type: isShopItem ? types.DISABLED : types.LINK,
            showTooltip: isShopItem,
            getTranslatedTooltip: (t) => t('myt_itemCard_shopItem_tooltip'),
            label: 'Ändra',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_update'),
            url: `/selling/edit/${item.itemId}`,
            gaLabel: isShopItem
                ? 'EditShopItem'
                : item.bidInformation.bidCount === 0
                  ? 'EditItem'
                  : 'AddText',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dela',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_share'),
            gaLabel: 'ShareActiveSellingItem',
            isVisible: () => true,
            isPrimaryAction: () => false,
            dispatch: (dispatch, _props, preferredLanguageCode) => {
                const shareLinkHref = toLocalizedUrl(
                    new URL(item.itemUrl, window.location.origin).href,
                    preferredLanguageCode
                );
                dispatch(
                    openShareModal({
                        shareLinkHref,
                        translationKeys,
                        shareTitle: item.listing.shortDescription,
                        shareImageUrl: item.listing.images.main.full
                    })
                );
            }
        }),
        common.sellSimilar,
        () => ({
            id: getId(item),
            type: types.LINK,
            url: getMessagingPageLink(
                item.seller.id,
                item.bidInformation?.leadingBidder?.id,
                item.itemId,
                {
                    traderaUserId: item.seller.id
                }
            ),
            label: 'Kontakta högstbjudande',
            getTranslatedLabel: (t) =>
                t('myt_itemCard_actions_contactHighestBidder'),
            dispatch: (dispatch, _props, _preferredLanguageCode, t) => {
                const itemSquareImage = getSrc(
                    item.listing.images.mainTemplate ?? '',
                    '{format}',
                    250
                );
                dispatch(
                    openNewMessageModal({
                        title: t('myt_itemCard_actions_contactHighestBidder'),
                        itemId: item.itemId,
                        senderId: item.seller.id,
                        recipientId: item.bidInformation.leadingBidder.id,
                        recipientAlias: item.bidInformation.leadingBidder.alias,
                        itemTitle: item.listing.shortDescription,
                        itemImageUrl: itemSquareImage,
                        itemSellerId: item.seller.id
                    })
                );
            },
            gaLabel: 'ContactHighestBidder',
            isVisible: () => {
                return (
                    featureToggles.isMessagingEnabled &&
                    item.bidInformation.bidCount > 0
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Avbryt annons',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_cancelItem'),
            dispatch: (dispatch) => {
                if (item.bidInformation.bidCount === 0) {
                    dispatch(myTraderaItemActions.cancelItem(item));
                } else {
                    dispatch(
                        openModal({
                            modalId: CANCEL_ITEM_MODAL_ID,
                            data: {
                                itemId: item.itemId
                            }
                        })
                    );
                }
            },
            gaLabel: 'EndItem',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.LINK,
            label: 'Marknadsför',
            getTranslatedLabel: (t) => t('upsell_extras_button'),
            url: `/selling/features/${item.itemId}`,
            isVisible: () => true,
            isPromotedSecondaryAction: () => true
        })
    ];
    return actions;
};

export const unsoldActions = (item, listViewType, memberId) => {
    const actions = [
        () => ({
            id: getId(item),
            type: types.LINK,
            label: item.listing.restart.hasRestartedChild
                ? 'Sälj liknande'
                : 'Lägg upp igen',
            getTranslatedLabel: (t) =>
                item.listing.restart.hasRestartedChild
                    ? t('myt_itemCard_actions_sellSimilar')
                    : t('myt_itemCard_actions_restart'),
            url: item.listing.restart.hasRestartedChild
                ? `/selling/sell-similar/${item.itemId}`
                : `/selling/restart/${item.itemId}`,
            gaLabel: 'Restart',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_hide'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellerHideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'HideUnsold',
            isVisible: () =>
                !item.transaction && !item.hiddenByMember?.includes(memberId),
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(item),
            type: types.DISPATCH,
            label: 'Visa',
            getTranslatedLabel: (t) => t('myt_itemCard_actions_show'),
            dispatch: (dispatch) => {
                dispatch(
                    myTraderaItemActions.sellerUnhideUnsold(
                        getIds(item),
                        listViewType
                    )
                );
            },
            gaLabel: 'UnhideUnsold',
            isVisible: () =>
                !item.transaction && item.hiddenByMember?.includes(memberId),
            isPrimaryAction: () => true
        })
    ];
    return actions;
};
